import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { getSupabase } from "../../utils";

export function useUserProfile() {
  const supabase = getSupabase();

  return useQuery({
    queryKey: ["user-profile"],
    queryFn: async () => {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        throw new Error("User not authenticated");
      }

      const { data: profile, error: profileError } = await supabase
        .from("app_users")
        .select("*")
        .eq("id", user.id)
        .single();

      if (profileError) {
        logger.error("Error fetching user profile", profileError);
        throw new Error(profileError.message);
      }

      return {
        user: {
          id: user.id,
          email: user.email,
          first_name: profile?.first_name,
          last_name: profile?.last_name,
          role: profile?.role,
          is_active: profile?.is_active,
          created_at: user.created_at,
          updated_at: profile?.updated_at,
        },
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateUserProfile() {
  const queryClient = useQueryClient();
  const supabase = getSupabase();

  return useMutation({
    mutationFn: async (updates: { first_name?: string; last_name?: string }) => {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from("app_users")
        .update(updates)
        .eq("id", user.id)
        .select()
        .single();

      if (error) {
        logger.error("Error updating user profile", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["user-profile"] });
      toast.success("Profile updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update profile");
      logger.error("Error updating profile", error);
    },
  });
}
